"use client";
import { Fragment } from "react";

import Image from "next/image";

import { Dialog, Transition } from "@headlessui/react";
import { useTranslations } from "next-intl";
import { BiCategory } from "react-icons/bi";
import { RiPagesLine } from "react-icons/ri";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { HTMLWrapper } from "./HtmlWrapper";


export const SlideOvers = ({ isOpen, onClose, selectedTheme }) => {
  const t = useTranslations();

  const { categories, description, image, name, pages, installs_count } =
    selectedTheme ?? {};

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-9999" onClose={onClose}>
        <div className="fixed inset-0 backdrop-blur-sm bg-gray-800/50 transition-all duration-300" />

        <div className="fixed inset-0 overflow-hidden ">
          <div className="absolute inset-0 overflow-hidden ">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl ">
                    <div className="px-4 py-6 sm:px-6">
                      <div className="flex items-start justify-between">
                        <h2
                          id="slide-over-heading"
                          className="text-base font-semibold leading-6 text-gray-900"
                        >
                          {t("themeInfo")}
                        </h2>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={() => onClose(false)}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">{t("closePanel")}</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="pb-1 sm:pb-6">
                        <div>
                          <div className="relative h-40 sm:h-56">
                            <Image
                              width={300}
                              height={300}
                              className="absolute h-full w-full object-cover"
                              src={`${image}`}
                              alt=""
                            />
                          </div>
                          <div className="mt-6 px-4 sm:mt-8 sm:flex sm:items-end sm:px-6">
                            <div className="sm:flex-1">
                              <div>
                                <div className="flex items-center">
                                  <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                                    {name}{" "}
                                  </h3>
                                  <span className="ml-2.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-green-400">
                                    <span className="sr-only">Online</span>
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500">
                                  {installs_count} {t("installs")}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 pb-5 pt-5 sm:px-0 sm:pt-0">
                        <dl className="space-y-8 px-4 sm:space-y-6 sm:px-6">
                          <div>
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0">
                              {t("aboutTheme")}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">
                              <HTMLWrapper htmlString={description} />
                            </dd>
                          </div>

                          <div>
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0">
                              {t("category")}
                            </dt>
                            <dd className="mt-1 grid text-sm text-gray-900 grid-cols-2 gap-2 ">
                              {categories?.map(({ id, name }) => (
                                <div className="flex flex-row" key={id}>
                                  <BiCategory
                                    className="w-6 h-6 mr-1"
                                    fill="#27bf0a"
                                  />
                                  {name}
                                </div>
                              ))}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0">
                              {t("pagesModules")}
                            </dt>
                            <dd className="mt-1 grid grid-cols-1 text-sm text-gray-900 ">
                              {pages?.map(({ id, sidebar_name, modules, }) => (
                                <li key={id}>
                                  <div className="flex flex-row mt-4">
                                    <RiPagesLine
                                      className="w-6 h-6 mr-1"
                                      fill="#27bf0a"
                                    />
                                    {sidebar_name}
                                  </div>
                                  <ol className="ps-5 mt-2 space-y-1 list-decimal list-inside">

                                    {modules?.map(({ id, sidebar_name }) => {
                                      return (
                                        <li key={id} className="flex flex-row">
                                          <div>
                                            <CheckCircleIcon
                                              className="w-6 h-6 mr-1"
                                              fill="#27bf0a"
                                            />
                                          </div>
                                          <span className="whitespace-nowrap">
                                            {sidebar_name}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ol>
                                </li>
                              ))}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
